/**
 * services/ConfluenceService.ts
 * Service for managing confluences
 *
 * Confluences are reusable market factors that affect trade decisions.
 * They are inherently neutral - polarity (positive/negative) is assigned
 * at the playbook level via PlaybookConfluenceLink.
 *
 * Extends BaseService to provide CRUD operations with status-based filtering
 * and category-based queries.
 */

import { BaseService } from './BaseService';
import type { Confluence } from '@/types/schema/Confluence';
import type { EntityStatus } from '@/types/shared/EntityStatus';
import { isEntityActive } from '@/types/shared/EntityStatus';

/**
 * Service for managing confluences
 *
 * Provides CRUD operations plus status-based and category-based filtering.
 * Inherits getAll(), get(), update(), softDelete() from BaseService.
 */
export class ConfluenceService extends BaseService<Confluence> {
  constructor() {
    super('confluences');
  }

  /**
   * Add a new confluence with default status
   *
   * Defaults to status: 'active' if not provided.
   *
   * @param data - Partial confluence data
   * @returns Promise resolving to the created confluence
   */
  override async add(data: Partial<Confluence>): Promise<Confluence> {
    return super.add({ status: 'active', ...data });
  }

  /**
   * Get confluences by status
   *
   * Filters confluences by the specified EntityStatus.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @param status - The status to filter by ('active' | 'inactive' | 'archived')
   * @returns Promise resolving to array of confluences with matching status
   */
  async getByStatus(status: EntityStatus): Promise<Confluence[]> {
    const allConfluences = await this.getAll();
    return allConfluences.filter(confluence => confluence.status === status);
  }

  /**
   * Get all active confluences
   *
   * Convenience method for getting confluences where status is 'active'.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @returns Promise resolving to array of active confluences
   */
  async getActiveConfluences(): Promise<Confluence[]> {
    const allConfluences = await this.getAll();
    return allConfluences.filter(confluence => isEntityActive(confluence.status));
  }

  /**
   * Get confluences by category
   *
   * Filters confluences by marketConditionCategoryId.
   * If categoryId is undefined, returns uncategorized items (where category is undefined).
   *
   * @param categoryId - The category ID to filter by, or undefined for uncategorized items
   * @returns Promise resolving to array of confluences in the specified category
   */
  async getByCategoryId(categoryId: string | undefined): Promise<Confluence[]> {
    const allConfluences = await this.getAll();
    return allConfluences.filter(confluence => confluence.marketConditionCategoryId === categoryId);
  }
}

// Export singleton instance
export const confluenceService = new ConfluenceService();
